import type { ParcelIntelReportData } from "../createPropertyReport";

export interface GeneralKvRow {
  key: string;
  label: string;
  value: string;
  /** Parcel # uses monospace styling */
  mono?: boolean;
}

function thousands(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function toTitleCase(s: string): string {
  return s.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

function fmtUsd(n: number): string {
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  });
}

function fmtSaleDate(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short"
  });
}

/** Mirrors legacy `parcelIntelSection` rows — omit empty fields; hide entirely when none remain. */
export function buildGeneralParcelRows(
  data: ParcelIntelReportData
): GeneralKvRow[] {
  const rows: GeneralKvRow[] = [];

  if (data.ownerName) {
    rows.push({
      key: "owner",
      label: "Owner",
      value: data.ownerName
    });
  }

  if (data.zoning) {
    const zone = data.landUse
      ? `${data.zoning} · ${data.landUse}`
      : data.zoning;
    rows.push({
      key: "zoning",
      label: "Zoning",
      value: zone
    });
  } else if (data.landUse) {
    rows.push({
      key: "landUse",
      label: "Land use",
      value: data.landUse
    });
  }

  if (data.acreage != null && Number.isFinite(data.acreage)) {
    rows.push({
      key: "acreage",
      label: "Recorded acreage",
      value: `${data.acreage.toFixed(2)} ac`
    });
  }

  if (data.lastSalePriceUsd != null && data.lastSalePriceUsd > 0) {
    const date = data.lastSaleDate ? ` · ${fmtSaleDate(data.lastSaleDate)}` : "";
    rows.push({
      key: "lastSale",
      label: "Last sale",
      value: `${fmtUsd(data.lastSalePriceUsd)}${date}`
    });
  } else if (data.lastSaleDate) {
    rows.push({
      key: "lastSale",
      label: "Last sale",
      value: fmtSaleDate(data.lastSaleDate)
    });
  }

  const county = data.county ? toTitleCase(data.county) : "";
  const locality = [county, data.state].filter(Boolean).join(", ");
  if (locality) {
    rows.push({
      key: "county",
      label: "County",
      value: locality
    });
  }

  if (data.parcelNumber) {
    rows.push({
      key: "parcelNumber",
      label: "Parcel #",
      value: data.parcelNumber,
      mono: true
    });
  }

  if (data.structures && data.structures.count > 0) {
    const noun = data.structures.count === 1 ? "structure" : "structures";
    rows.push({
      key: "existing",
      label: "Existing",
      value: `${data.structures.count} ${noun} · ${thousands(
        data.structures.totalFootprintSqFt
      )} ft² footprint`
    });
  }

  return rows;
}
